import { Loading } from "components/ui/Loading/Loading";
import { usePipelineEmailTimeline } from "hooks/use-pipeline-email-api";
import type { FC } from "react";
import { formatShortDateTime } from "utils/relative-time";

export type EmailTimelineProps = {
  pipelineId: string;
  /** 「メール詳細で見る」のリンク先を提供する callback。未指定なら非表示 */
  onOpenDetail?: (item: {
    sentEmailId: string | null;
    replyEmailId: string | null;
  }) => void;
};

/**
 * Sales Board カード展開時のメールタイムライン。sent / received を時系列で描画する。
 * @param {EmailTimelineProps} props props
 * @return {JSX.Element} タイムライン
 */
export const EmailTimeline: FC<EmailTimelineProps> = ({
  pipelineId,
  onOpenDetail,
}) => {
  const { items, loading, error } = usePipelineEmailTimeline({ pipelineId });

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loading size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-rose-600 py-2">
        タイムラインの取得に失敗しました
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-2">
        このパイプラインに紐付くメールはまだありません
      </div>
    );
  }

  return (
    <ul className="space-y-1 text-xs">
      {items.map(item => {
        const key = `${item.direction}-${item.sentEmailId ?? item.replyEmailId}`;
        const icon = item.direction === "sent" ? "📤" : "📥";
        const labelColor =
          item.direction === "sent" ? "text-slate-600" : "text-emerald-700";
        return (
          <li key={key} className="flex items-start gap-2">
            <span className="shrink-0">{icon}</span>
            <span className="shrink-0 text-slate-500 font-mono">
              {formatShortDateTime(item.occurredAt)}
            </span>
            <div className="flex-1 min-w-0">
              <div className={`font-medium truncate ${labelColor}`}>
                {item.subject || "(件名なし)"}
              </div>
              <div className="text-slate-500 truncate">{item.fromTo}</div>
              {item.bodyPreview && (
                <div className="text-slate-400 truncate">
                  {item.bodyPreview}
                </div>
              )}
            </div>
            {onOpenDetail && (
              <button
                type="button"
                className="text-sky-600 hover:underline shrink-0"
                onClick={() =>
                  onOpenDetail({
                    sentEmailId: item.sentEmailId,
                    replyEmailId: item.replyEmailId,
                  })
                }
              >
                詳細
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
};
